import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/mergeMap';
import { fg } from '@atlassian/jira-feature-gating';
import { Capability } from '../../common/capability/index.tsx';
import {
	type CardClickAction,
	cardToggleSelection,
	cardSingleSelect,
	CARD_CLICK,
	cardMultiSelect,
} from '../../state/actions/card/index.tsx';
import { openIssueModal, openPlanIssueModal } from '../../state/actions/issue/modal/index.tsx';
import { getCapability } from '../../state/selectors/capability/capability-selectors.tsx';
import { getCardShiftSelectionIds } from '../../state/selectors/card/card-multi-selection.tsx';
import { getCardSelection } from '../../state/selectors/card/card-selectors.tsx';
import { isBoardIssueAChild } from '../../state/selectors/issue/board-issue-selectors.tsx';
import { getIssueById } from '../../state/selectors/issue/issue-selectors.tsx';
import {
	getIsCMPBoard,
	getIsIncrementPlanningBoard,
} from '../../state/selectors/software/software-selectors.tsx';
import {
	issueColumnIdSelector,
	issueSwimlaneIdSelector,
} from '../../state/selectors/work/work-selectors.tsx';
import type { Action, ActionsObservable, MiddlewareAPI } from '../../state/types.tsx';

export default function cardClickEpic(action$: ActionsObservable, store: MiddlewareAPI) {
	return action$.ofType(CARD_CLICK).mergeMap((action: CardClickAction): Observable<Action> => {
		const state = store.getState();
		const { issueId, withCmd, withShift } = action.payload;

		if (withCmd) {
			const cardSelection = getCardSelection(state);
			const isCMPBoard = getIsCMPBoard(state);
			const isMultiSelectAcrossSwimlanesEnabled = getCapability(state)(
				Capability.MULTISELECT_CARD_ACROSS_SWIMLANES,
			);
			const isAdding = cardSelection.indexOf(action.payload.issueId) === -1;

			// We only allow issues with the same column, swimlane and card type to be selected together in a CMP board
			if (isCMPBoard && cardSelection.length !== 0 && isAdding) {
				const getColumnIdByIssue = issueColumnIdSelector(state);
				const getSwimlaneIdByIssue = issueSwimlaneIdSelector(state);
				const isIssueAChild = isBoardIssueAChild(state);
				const existingSelectedId = cardSelection[0];

				const sameColumnId = getColumnIdByIssue(existingSelectedId) === getColumnIdByIssue(issueId);
				const sameSwimlaneId =
					getSwimlaneIdByIssue(existingSelectedId) === getSwimlaneIdByIssue(issueId);
				const sameCardType = isIssueAChild(existingSelectedId) === isIssueAChild(issueId);
				const sameCardGroup =
					getIssueById(state, existingSelectedId)?.parentId ===
					getIssueById(state, issueId).parentId;

				if (
					!sameColumnId ||
					!sameSwimlaneId ||
					!sameCardType ||
					(isIssueAChild(issueId) && !sameCardGroup)
				) {
					return Observable.empty<never>();
				}
			}

			// Don't allow issues in the same swimlane to be selected together when
			// MULTISELECT_CARD_ACROSS_SWIMLANES capability is false
			if (!isMultiSelectAcrossSwimlanesEnabled && cardSelection.length !== 0 && isAdding) {
				const getSwimlaneIdByIssue = issueSwimlaneIdSelector(state);
				const existingSelectedId = cardSelection[0];

				const sameSwimlaneId =
					getSwimlaneIdByIssue(existingSelectedId) === getSwimlaneIdByIssue(issueId);

				if (!sameSwimlaneId) {
					return Observable.empty<never>();
				}
			}

			return Observable.of(cardToggleSelection(issueId));
		}

		if (withShift) {
			const rangeSelection = getCardShiftSelectionIds(state)(issueId);

			if (rangeSelection) {
				return Observable.of(cardMultiSelect(rangeSelection));
			}
		}

		// open bento + select card
		const issue = getIssueById(state, issueId);

		const isIncrementPlanningBoard = getIsIncrementPlanningBoard(state);

		if (isIncrementPlanningBoard) {
			if (fg('issue_view_in_program_board')) {
				return Observable.from([cardSingleSelect(issueId), openPlanIssueModal(issueId.toString())]);
			}
			Observable.from([cardSingleSelect(issueId)]);
		}

		return Observable.from([
			cardSingleSelect(issueId),
			openIssueModal(issue.key, action.payload.externalAction),
		]);
	});
}
